/**
 * PRODUCT_DEV-1238 パスワード再発行の依頼（ユーザー管理の一番上・管理者トップの印・メニューの赤い点）。
 *
 * - 会社の `passwordReissue` が ON で、ユーザー管理ができる人（`users.manage`）だけが取りに行く
 * - 60秒ごと（タブが裏のときは止める。表に戻ったらすぐ取る）。依頼は急ぎではないので通知より間隔を空ける
 * - 印・一覧・メニューが同じ値を見るよう、取りに行くのは1本にまとめる（モジュールで持つ）
 * - 裏で回るので 403 などでも画面を飛ばさない（useNewNotifications と同じ）
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { createApiOptions } from "@shared-ui/utils/api";
import type {
  AdminPasswordReissueRequest,
  AdminPasswordReissueRequestsResponse,
} from "@shared-domain/auth-service/contract";
import { useRouteLoaderData } from "react-router";
import type { RootLoaderData } from "@/loaders/root-loader";
import { usePageVisible } from "@/hooks/usePageVisible";
import { useOptionalTenantFeatureFlag } from "@/hooks/useTenantFeatureFlag";
import { API_ADMIN_PASSWORD_REISSUE_REQUESTS } from "@/utils/const";
import { checkCan } from "@/utils/role";

const POLL_INTERVAL_MS = 60_000;

type Snapshot = {
  tenantId: string | null;
  requests: AdminPasswordReissueRequest[];
  pendingCount: number;
};

const EMPTY: Snapshot = { tenantId: null, requests: [], pendingCount: 0 };

let snapshot: Snapshot = EMPTY;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => snapshot;

let pollers = 0;
// 取りに行った順の番号。発行・取り消しのあとに、それより前に出た取得の応答が届いても捨てる
// （一覧は GSI で強い整合性が無いので、直後に取り直すと古い内容が返ることもある）
let seq = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let activeTenantId: string | null = null;

const fetchOnce = async (tenantId: string): Promise<void> => {
  const mine = ++seq;
  try {
    const options = await createApiOptions({
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const res = await fetch(`${API_ADMIN_PASSWORD_REISSUE_REQUESTS}?tenantId=${tenantId}`, options);
    if (!res.ok || activeTenantId !== tenantId) return;
    const body = (await res.json()) as AdminPasswordReissueRequestsResponse;
    if (mine !== seq) return;
    snapshot = {
      tenantId,
      requests: body.requests ?? [],
      pendingCount: body.pendingCount ?? 0,
    };
    emit();
  } catch {
    // 次の回で取り直す
  }
};

const pendingOf = (requests: AdminPasswordReissueRequest[]) =>
  requests.filter((r) => r.status !== "issued").length;

/** コードを出した直後に、一覧を手元で「発行済み」にする（もう一度押してコードを無効にしないように）。 */
export const markReissueIssued = (userId: string, expiresAt: number) => {
  seq += 1;
  const requests = snapshot.requests.map((r) =>
    r.userId === userId ? { ...r, status: "issued" as const, expiresAt, tooManyFailures: false } : r,
  );
  snapshot = { ...snapshot, requests, pendingCount: pendingOf(requests) };
  emit();
};

/** 取り消した直後に、一覧から手元で外す。 */
export const markReissueDismissed = (userId: string) => {
  seq += 1;
  const requests = snapshot.requests.filter((r) => r.userId !== userId);
  snapshot = { ...snapshot, requests, pendingCount: pendingOf(requests) };
  emit();
};

/** テストで状態を戻す。 */
export const _resetPasswordReissueStore = () => {
  snapshot = EMPTY;
  pollers = 0;
  seq = 0;
  activeTenantId = null;
  if (timer) clearInterval(timer);
  timer = null;
};

export function usePasswordReissueRequests(): {
  enabled: boolean;
  requests: AdminPasswordReissueRequest[];
  pendingCount: number;
  refresh: () => Promise<void>;
} {
  // root の外（メニューだけを描くテストなど）でも落とさない。無ければ取りに行かない
  const auth = (useRouteLoaderData("root") as RootLoaderData | undefined)?.auth ?? null;
  const visible = usePageVisible();
  const flagOn = useOptionalTenantFeatureFlag("passwordReissue");
  const tenantId = auth?.currentTenantId ?? null;
  const enabled = !!auth && !!tenantId && flagOn && checkCan(auth, "users.manage");

  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!enabled || !tenantId || !visible) return;
    if (activeTenantId !== tenantId) {
      activeTenantId = tenantId;
      snapshot = { ...EMPTY, tenantId };
      emit();
    }
    pollers += 1;
    if (pollers === 1) {
      void fetchOnce(tenantId);
      timer = setInterval(() => void fetchOnce(tenantId), POLL_INTERVAL_MS);
    }
    return () => {
      pollers -= 1;
      if (pollers === 0 && timer) {
        clearInterval(timer);
        timer = null;
      }
    };
  }, [enabled, tenantId, visible]);

  const refresh = useCallback(async () => {
    if (enabled && tenantId) await fetchOnce(tenantId);
  }, [enabled, tenantId]);

  const mine = enabled && current.tenantId === tenantId ? current : EMPTY;
  return { enabled, requests: mine.requests, pendingCount: mine.pendingCount, refresh };
}
